import { Box, Button, Stack, Text, useToast } from "@chakra-ui/react";
import { isEqual } from "lodash";
import { type MutableRefObject, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { FrameworkAgreementDto, FrameworkAgreementPatchDto, FrameworkValueReminderDto } from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { coordinatorRegistry, useEditor } from "../../common/editing/save-coordinator";
import { frameworkValueReminderFailure } from "../settings/Notifications/frameworkValueReminderFailure";
import { FrameworkValueReminderCard } from "../settings/Notifications/FrameworkValueReminderSetting";
import { FrameworkValueReminderForm } from "../settings/Notifications/FrameworkValueReminderSettingForm";
import { frameworkValueReminders } from "../settings/Notifications/NotificationSettings";
import { SectionCard } from "./SectionCard";

export type ReminderFailureHandler = MutableRefObject<((error: unknown) => void) | null>;

type PendingSave = { failureTitle: string; settle: (saved: boolean) => void };

export const FrameworkValueRemindersSection = ({
  frameworkAgreementId,
  canEdit,
  failureHandler,
}: {
  frameworkAgreementId: string;
  canEdit: boolean;
  failureHandler: ReminderFailureHandler;
}) => {
  const { t } = useTranslation();
  const toast = useToast();
  const { selectedOrg } = useLoggedInWithOrgContextState();
  const { state, updateField, entityStatus } = useEditor<FrameworkAgreementDto, FrameworkAgreementPatchDto>(
    frameworkAgreementId,
  );
  const [isAdding, setAdding] = useState(false);
  const isSaving = entityStatus === "pending" || entityStatus === "saving";
  const own = state.valueReminders ?? [];
  const defaults = frameworkValueReminders(selectedOrg.defaultNotificationSettings);

  const pending = useRef<PendingSave | null>(null);
  failureHandler.current = (error) => toast(frameworkValueReminderFailure(pending.current?.failureTitle ?? "", error));

  const previousStatus = useRef(entityStatus);
  useEffect(() => {
    const before = previousStatus.current;
    previousStatus.current = entityStatus;
    if (pending.current === null || before === entityStatus) return;
    if (entityStatus === "saved") {
      pending.current.settle(true);
      pending.current = null;
    } else if (entityStatus === "error") {
      coordinatorRegistry.get(frameworkAgreementId)?.discardPendingChanges();
      pending.current.settle(false);
      pending.current = null;
    }
  }, [entityStatus, frameworkAgreementId]);

  const save = (reminders: FrameworkValueReminderDto[], failureTitle: string) =>
    new Promise<boolean>((settle) => {
      pending.current = { failureTitle, settle };
      updateField({ valueReminders: reminders } as Partial<FrameworkAgreementPatchDto>);
    });

  return (
    <SectionCard
      title={t("Reminders")}
      action={
        canEdit && !isAdding ? (
          <Button size="sm" colorScheme="teal" onClick={() => setAdding(true)}>
            {t("New reminder")}
          </Button>
        ) : undefined
      }
    >
      <Stack spacing="5" px={{ base: "4", md: "5" }} py="4">
        <Box>
          <Text fontWeight="bold" fontSize="sm" pb="1">
            {t("Reminders for this framework agreement")}
          </Text>
          {own.length === 0 && !isAdding && (
            <Text fontSize="sm" color="smMuted">
              {t("No reminders added")}
            </Text>
          )}
          {own.map((reminder, i) => (
            <FrameworkValueReminderCard
              key={i}
              setting={reminder}
              isDeleting={isSaving}
              onDelete={
                canEdit
                  ? () => save(own.filter((r) => !isEqual(r, reminder)), t("The reminder was not deleted"))
                  : undefined
              }
            />
          ))}
          {canEdit && (
            <FrameworkValueReminderForm
              organizationId={selectedOrg.id}
              existing={own}
              isSaving={isSaving}
              isOpen={isAdding}
              onClose={() => setAdding(false)}
              onSave={(reminder) => save([...own, reminder], t("The reminder was not saved"))}
            />
          )}
        </Box>
        <Box>
          <Text fontWeight="bold" fontSize="sm" pb="1">
            {t("Reminders for all framework agreements")}
          </Text>
          {defaults.length === 0 ? (
            <Text fontSize="sm" color="smMuted">
              {t("No reminders added")}
            </Text>
          ) : (
            defaults.map((reminder, i) => <FrameworkValueReminderCard key={i} setting={reminder} />)
          )}
          <Text fontSize="sm" color="smMuted" pt="1">
            {t("An administrator changes these under Settings → Notification Settings.")}
          </Text>
        </Box>
      </Stack>
    </SectionCard>
  );
};
